// src/components/admin/CreateTestBookingData.jsx
import React, { useState } from 'react';
import { getFirestore, collection, addDoc, Timestamp } from 'firebase/firestore';
import { useTenant } from '../../context/TenantContext';
import { Calendar, User, Clock } from 'lucide-react';

const CreateTestBookingData = () => {
  const { tenantId } = useTenant();
  const [loading, setLoading] = useState(false);
  const [created, setCreated] = useState(false);

  const testBookingRequests = [
    {
      clientDetails: {
        firstName: 'Sarah',
        lastName: 'Johnson',
        email: 'sarah.johnson@email.com',
        phone: '+44 7700 900123',
        dateOfBirth: '1985-03-15',
        notes: 'First time patient, referred by GP for lower back pain',
        gdprConsent: true,
        consentTimestamp: new Date().toISOString()
      },
      clinicianId: 'dr-smith',
      serviceId: 'Physiotherapy',
      requestedSlot: {
        start: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toISOString(), // 2 days from now
        end: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000 + 60 * 60 * 1000).toISOString() // +1 hour
      },
      status: 'pending',
      source: 'public_booking'
    },
    {
      clientDetails: {
        firstName: 'Michael',
        lastName: 'Brown',
        email: 'michael.brown@email.com',
        phone: '+44 7700 900456',
        dateOfBirth: '1978-08-22',
        notes: 'Follow-up appointment for shoulder rehabilitation',
        gdprConsent: true,
        consentTimestamp: new Date().toISOString()
      },
      clinicianId: 'dr-jones',
      serviceId: 'Sports Therapy',
      requestedSlot: {
        start: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString(), // 3 days from now
        end: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000 + 45 * 60 * 1000).toISOString() // +45 mins
      },
      status: 'approved',
      source: 'public_booking',
      approvedAt: Timestamp.now(),
      approvedBy: 'admin-user-id'
    },
    {
      clientDetails: {
        firstName: 'Emma',
        lastName: 'Wilson',
        email: 'emma.wilson@email.com',
        phone: '+44 7700 900789',
        dateOfBirth: '1992-11-05',
        notes: 'Urgent appointment needed for knee injury from running',
        gdprConsent: true,
        consentTimestamp: new Date().toISOString()
      },
      clinicianId: 'dr-smith',
      serviceId: 'Assessment',
      requestedSlot: {
        start: new Date(Date.now() + 1 * 24 * 60 * 60 * 1000).toISOString(), // 1 day from now
        end: new Date(Date.now() + 1 * 24 * 60 * 60 * 1000 + 90 * 60 * 1000).toISOString() // +90 mins
      },
      status: 'pending',
      source: 'public_booking'
    },
    {
      clientDetails: {
        firstName: 'James',
        lastName: 'Taylor',
        email: 'james.taylor@email.com',
        phone: '+44 7700 900012',
        dateOfBirth: '1965-07-18',
        notes: 'Regular maintenance appointment for chronic condition',
        gdprConsent: true,
        consentTimestamp: new Date().toISOString()
      },
      clinicianId: 'dr-jones',
      serviceId: 'Massage Therapy',
      requestedSlot: {
        start: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000).toISOString(), // 5 days from now
        end: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000 + 60 * 60 * 1000).toISOString() // +1 hour
      },
      status: 'declined',
      source: 'public_booking',
      declinedAt: Timestamp.now(),
      declinedBy: 'admin-user-id',
      adminNotes: 'Clinician unavailable on requested date'
    }
  ];

  const createTestData = async () => {
    if (!tenantId) {
      alert('No tenant context available');
      return;
    }

    try {
      setLoading(true);
      const db = getFirestore();
      
      for (const request of testBookingRequests) {
        const docData = {
          ...request,
          tenantId,
          createdAt: Timestamp.now()
        };
        
        await addDoc(
          collection(db, 'tenants', tenantId, 'appointmentRequests'),
          docData
        );
      }
      
      setCreated(true);
      console.log('✅ Test booking requests created successfully');
      
    } catch (error) {
      console.error('Error creating test data:', error);
      alert('Failed to create test data: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  if (created) {
    return (
      <div className="p-6 bg-green-50 border border-green-200 rounded-lg">
        <div className="flex items-center space-x-3">
          <Calendar className="w-6 h-6 text-green-600" />
          <div>
            <h3 className="text-lg font-semibold text-green-900">Test Data Created!</h3>
            <p className="text-green-700">
              4 test booking requests have been added. Refresh the Booking Requests tab to see them.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-blue-50 border border-blue-200 rounded-lg">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Calendar className="w-6 h-6 text-blue-600" />
          <div>
            <h3 className="text-lg font-semibold text-blue-900">Create Test Booking Data</h3>
            <p className="text-blue-700">
              Add sample booking requests to test the admin interface
            </p>
          </div>
        </div>
        
        <button
          onClick={createTestData}
          disabled={loading || !tenantId}
          className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
        >
          {loading ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              <span>Creating...</span>
            </>
          ) : (
            <>
              <User className="w-4 h-4" />
              <span>Create Test Data</span>
            </>
          )}
        </button>
      </div>
      
      <div className="mt-4 text-sm text-blue-600">
        <p><strong>Will create:</strong></p>
        <ul className="mt-2 space-y-1">
          <li>• 2 Pending requests (Sarah Johnson, Emma Wilson)</li>
          <li>• 1 Approved request (Michael Brown)</li>
          <li>• 1 Declined request (James Taylor)</li>
        </ul>
      </div>
    </div>
  );
};

export default CreateTestBookingData;
